import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { FileWarning, FileCheck2 } from "lucide-react";
import { API } from "./shared";

const box = (ok) => ({ marginTop: 18, padding: "14px 16px", borderRadius: 12, border: `1px solid ${ok ? "#A7F3D0" : "#FCD34D"}`, background: ok ? "#ECFDF5" : "#FFFBEB", fontSize: 13 });
const ctl = { border: "1px solid #CBD5E1", borderRadius: 8, padding: "8px 10px", fontSize: 13, fontFamily: "inherit", minWidth: 0, width: "100%", boxSizing: "border-box" };
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

export function useEwayBill(jobId) {
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/jobs/${jobId}/ewaybill`).then(r => setData(r.data)).catch(() => setData(null));
  useEffect(() => { if (jobId) load(); }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, load];
}

function TransportForm({ jobId, onDone, mode, initial }) {
  const [f, setF] = useState({ from_pincode: initial?.from_pincode || "", distance_km: initial?.distance_km || "",
    vehicle_no: initial?.vehicle_no || "", transporter_id: initial?.transporter_id || "" });
  const zoho = mode === "zoho";
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const body = { from_pincode: f.from_pincode.trim(), distance_km: f.distance_km ? Number(f.distance_km) : null,
        vehicle_no: f.vehicle_no.trim() || null, transporter_id: f.transporter_id.trim() || null };
      const { data } = await axios.post(`${API}/${zoho ? "admin/" : ""}jobs/${jobId}/ewaybill/${zoho ? "zoho" : "details"}`, body);
      toast.success(zoho ? `E-way bill ${data.number} generated` : "Details sent. GoPrintPack will generate the e-way bill");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not generate e-way bill"); } finally { setBusy(false); }
  };
  return (
    <div data-testid={zoho ? "ewb-zoho-form" : "ewb-details-form"} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8, marginTop: 10 }}>
      <input data-testid="ewb-from-pincode" placeholder="From pincode" value={f.from_pincode} onChange={set("from_pincode")} maxLength={6} inputMode="numeric" style={ctl}/>
      <input data-testid="ewb-distance" placeholder={zoho ? "Distance (km)" : "Distance km (optional)"} type="number" min="1" value={f.distance_km} onChange={set("distance_km")} style={ctl}/>
      <input data-testid="ewb-vehicle" placeholder="Vehicle no." value={f.vehicle_no} onChange={set("vehicle_no")} style={ctl}/>
      <input data-testid="ewb-transporter" placeholder="or Transporter ID" title="Transporter GSTIN / ID if the vehicle is not known yet" value={f.transporter_id} onChange={set("transporter_id")} style={ctl}/>
      <button data-testid={zoho ? "ewb-zoho-submit" : "ewb-details-submit"} className="primary-btn"
        disabled={busy || f.from_pincode.length !== 6 || (zoho && !f.distance_km) || !(f.vehicle_no || f.transporter_id)} onClick={submit}>
        {busy ? (zoho ? "Generating…" : "Sending…") : (zoho ? "Generate via Zoho" : initial ? "Update details" : "Send to GoPrintPack")}
      </button>
    </div>
  );
}

function ManualForm({ jobId, onDone }) {
  const [num, setNum] = useState("");
  const [till, setTill] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/jobs/${jobId}/ewaybill`, { number: num, valid_till: till || null });
      toast.success("E-way bill number saved");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="ewb-manual-form" style={{ display: "grid", gridTemplateColumns: "2fr 1fr auto", gap: 8, marginTop: 10 }}>
      <input data-testid="ewb-manual-number" placeholder="12-digit e-way bill number" value={num} onChange={e => setNum(e.target.value.replace(/[^\d\s]/g, ""))} style={ctl}/>
      <input data-testid="ewb-manual-valid" type="date" value={till} onChange={e => setTill(e.target.value)} style={ctl} title="Valid till (optional)"/>
      <button data-testid="ewb-manual-submit" className="secondary-btn" disabled={busy || num.replace(/\s/g, "").length !== 12} onClick={submit}>Save number</button>
    </div>
  );
}

export function EwayBillPanel({ job, role, onChange }) {
  const [d, reload] = useEwayBill(job.id);
  if (!d || !d.required) return null;
  const done = () => { reload(); onChange && onChange(); };
  const ewb = d.ewaybill;
  return (
    <div data-testid="ewb-panel" style={box(!!ewb)}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", fontWeight: 700, color: ewb ? "#065F46" : "#92400E" }}>
        {ewb ? <FileCheck2 size={16}/> : <FileWarning size={16}/>}
        {ewb ? <>E-way bill <span data-testid="ewb-number">{ewb.number}</span></> : "E-way bill required before dispatch"}
      </div>
      <div style={{ color: "#475569", marginTop: 4, fontSize: 12.5 }}>
        Consignment value {inr(d.value_inr)} (incl. GST) is above the {inr(d.limit_inr)} {d.intra_state ? "intra-state" : "inter-state"} limit.
        {ewb ? ` Source: ${ewb.source === "zoho" ? "Zoho Books" : "entered by admin"}${ewb.valid_till ? ` · valid till ${String(ewb.valid_till).slice(0, 10)}` : ""}. It's printed on the blind label.`
             : " Dispatch is blocked until the number is added."}
      </div>
      {!ewb && d.auto?.status === "failed" && (
        <div data-testid="ewb-auto-error" style={{ marginTop: 8, fontSize: 12, color: "#B91C1C" }}>Automatic Zoho e-way bill failed: {d.auto.error}</div>
      )}
      {!ewb && job.status === "awarded" && (role === "admin" ? (
        <>
          {d.details
            ? <div data-testid="ewb-details-view" style={{ marginTop: 10, padding: "8px 10px", background: "#fff", border: "1px solid #E2E8F0", borderRadius: 8, fontSize: 12.5 }}>
                <b>Printer's transport details</b> ({String(d.details.submitted_at).slice(0, 10)}): pickup pincode {d.details.from_pincode}
                {d.details.distance_km ? ` · ${d.details.distance_km} km` : ""}{d.details.vehicle_no ? ` · vehicle ${d.details.vehicle_no}` : ""}{d.details.transporter_id ? ` · transporter ${d.details.transporter_id}` : ""}
              </div>
            : <div style={{ fontSize: 11.5, color: "#92400E", marginTop: 8 }}>Waiting for the printer's transport details.</div>}
          <div style={{ fontSize: 11.5, color: "#64748b", marginTop: 8 }}>Generate on the Tejas Industries invoice (Bill From: Tejas Industries · Dispatch From: printer's pickup pincode · Ship To: buyer).</div>
          {d.zoho_configured && <TransportForm key={d.details?.submitted_at || "z"} jobId={job.id} onDone={done} mode="zoho" initial={d.details}/>}
          <ManualForm jobId={job.id} onDone={done}/>
        </>
      ) : (
        <>
          <div data-testid="ewb-printer-note" style={{ fontSize: 12.5, color: "#92400E", marginTop: 8, fontWeight: 600 }}>
            GoPrintPack will generate the e-way bill. Please share your vehicle number or transporter ID and pickup pincode.
          </div>
          {d.details && <div data-testid="ewb-details-sent" style={{ fontSize: 11.5, color: "#065F46", marginTop: 4 }}>Details sent on {String(d.details.submitted_at).slice(0, 10)}. {d.zoho_configured && !d.auto ? "GoPrintPack is generating the e-way bill automatically; refresh in a minute. " : "We'll add the e-way bill number here. "}You can update them below.</div>}
          <TransportForm jobId={job.id} onDone={done} mode="details" initial={d.details}/>
          <div style={{ fontSize: 11, color: "#64748b", marginTop: 6 }}>Don't create an e-way bill yourself; only one is allowed per shipment.</div>
        </>
      ))}
    </div>
  );
}
